import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NavigationLock from "./NavigationLock";

import { useTranslation } from "common/locales";
import RelevantFilesList from "features/programmesActions/RelevantFilesList";
import {
  addFilledOut,
  fetchAllProgrammeActions,
  ProgrammeActions,
  selectProgrammeActionsById,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  setQuestionnaireFinished,
  setQuestionnaireStarted,
} from "model/questionnaireStartedSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import ActionList from "./ActionList";
import CenteredTextBox from "./CenteredTextBox";
import StepCard from "./FirstStepCard";

/**
 * Web-only component for displaying programme details.
 * This component is used only on desktop/web view.
 * Mobile continues to use ProgrammeDetailsDrawer in the drawer.
 */
export default function ProgrammeDetailsWeb() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { programmeId } = useParams<{ programmeId: string }>();
  const REFRESH_INTERVAL = 1 * 1000;
  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const [viewMore, setViewMore] = useState(false);
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.details);
  const history = useHistory();

  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );

  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );
  const hasSelectedActions = !(
    !programmeActions?.selectedActions ||
    Object.keys(programmeActions.selectedActions).length === 0
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // Returning false allows navigation, returning a string shows a prompt
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      programmeId
    ) && !hasSelectedActions;

  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadProgrammesAndActions = async () => {
        if (!loading) {
          await dispatch(fetchAllProgrammeActions());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadProgrammesAndActions,
        REFRESH_INTERVAL
      );
      // clear interval when there no fields with invalid data
      if (hasSelectedActions) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loading,
    hasSelectedActions,
    REFRESH_INTERVAL,
    programmeActions,
  ]);

  if (loading || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  const maxHeight = viewMore ? "auto" : 200;

  const hasQuestionnaireUrl = !!programmeActions?.questionnaireUrl;

  // Display mode 1: Actions have been selected/submitted
  if (hasSelectedActions) {
    return (
      <Box>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          t("action.plan.submitted.thank.you"),
          t,
          false
        )}
        <ActionList rawSelectedActions={programmeActions.selectedActions} />
      </Box>
    );
  }

  // Display mode 2: Questionnaire is being filled out
  if (hasQuestionnaireUrl && questionnaireStarted.questionnaireStarted) {
    return (
      <Box>
        <NavigationLock
          when={questionnaireStarted.questionnaireStarted}
        ></NavigationLock>

        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}

        <RelevantFilesList />
      </Box>
    );
  }

  // Display mode 3: Questionnaire not started yet
  if (hasQuestionnaireUrl && !questionnaireStarted.questionnaireStarted) {
    return (
      <Box>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box sx={{ maxWidth: "600px", width: "100%" }}>
            <StepCard
              stepNumber={1}
              stepTitle="Plan your actions"
              stepDescription="The first step is to plan actions that feel manageable and will make a real impact on your farm."
              buttonText="PLAN YOUR ACTIONS"
              daysLeft={122}
              status="In progress"
              onButtonClick={() => {
                dispatch(setQuestionnaireStarted(true));
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // Fallback: Just show programme details
  if (programmeActions) {
    return (
      <Box>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
      </Box>
    );
  }

  return <></>;
}

const MoreLessButton = styled("span")(
  ({ theme }) => css`
    color: var(--Dahlia-700, #2e7831);
    font-feature-settings: "liga" off, "clig" off;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  `
);

/**
 * Renders programme details with banner header (matching programmes list design)
 */
const ProgrammeDetailsWithBanner = (
  programmeActions: ProgrammeActions,
  viewMore: boolean,
  maxHeight: string | number,
  setViewMore: (arg: boolean) => void,
  userName: string,
  text: string,
  t: (a: string) => string,
  readMoreOrLessButton = true
) => {
  return (
    <Box sx={{ bgcolor: "white", p: 3 }}>
      {/* Banner Header */}
      <Box
        sx={{
          height: "120px",
          borderRadius: "8px",
          mb: 3,
          ...(programmeActions.guid === "6vczLKixKSwPkIFP3JkkGD"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/lidl-sustainable-beef-banner.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#395499",
              }
            : programmeActions.guid === "3UqQUHVbKY5CMBthVL5Ltq"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/Pattemores.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "repeat",
                backgroundColor: "#ffffffff",
              }
            : programmeActions.guid === "5OPgsM2LDKgFHqhLO4Kzpv"
            ? {
                backgroundImage:
                  'url("https://ruumi-dashboard-assets.ruumi.io/County-Milk-2.png")',
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "repeat",
                backgroundColor: "#ffffffff",
              }
            : {
                backgroundColor: "#1C4321",
              }),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {programmeActions.guid !== "6vczLKixKSwPkIFP3JkkGD" &&
          programmeActions.guid !== "3UqQUHVbKY5CMBthVL5Ltq" &&
          programmeActions.guid !== "5OPgsM2LDKgFHqhLO4Kzpv" && (
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 600,
                textAlign: "center",
                px: 2,
              }}
            >
              {programmeActions.programmeName}
            </Typography>
          )}
      </Box>

      {/* Programme Name */}
      <Grid
        container
        spacing={3}
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Grid item xs={12} sm={12} sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h4"
            gutterBottom
            alignContent={"center"}
          >
            {programmeActions.programmeName}
          </Typography>
        </Grid>

        {/* Text Content */}
        {text && (
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <CenteredTextBox
              text={text.replace("FARMER_NAME", userName)}
              useMaxSpace={viewMore}
              maxHeight={maxHeight}
              lineHeight={20}
            />
          </Grid>
        )}
        {!text && (
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <CenteredTextBox
              text={t("programme.in.developement")}
              useMaxSpace={viewMore}
              maxHeight={maxHeight}
              lineHeight={20}
            />
          </Grid>
        )}

        {/* Read More/Less Button */}
        {text && (
          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            {readMoreOrLessButton && (
              <Button variant="text" onClick={() => setViewMore(!viewMore)}>
                <MoreLessButton>
                  {viewMore ? "Read less" : "Read more"}
                </MoreLessButton>
              </Button>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
